import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { isValidToken } from '../utils/tokenValidation';

import { getApiBaseUrl } from '../utils/environmentConfig';

const API_URL = process.env.REACT_APP_API_BASE_URL || getApiBaseUrl();

export const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const { isAuthenticated, authToken } = useContext(AuthContext);

    const fetchNotifications = async () => {
        // Don't fetch if user is not authenticated
        if (!isAuthenticated) {
            console.log('🔍 useNotifications: User not authenticated, skipping notifications fetch');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            console.log('🔍 useNotifications: Fetching notifications - Token present:', !!token);
            console.log('🔍 useNotifications: Token length:', token?.length);
            console.log('🔍 useNotifications: Is authenticated:', isAuthenticated);
            
            if (!token) {
                console.log('🔍 useNotifications: No token available, skipping notifications fetch');
                setLoading(false);
                return;
            }

            // Validate token format before making request
            if (!isValidToken(token)) {
                console.log('🔍 useNotifications: Invalid token format, skipping notifications fetch');
                setLoading(false);
                return;
            }

            console.log('🔍 useNotifications: Making notifications API request...');
            const response = await axios.get(`${API_URL}/api/notifications`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            console.log('🔍 useNotifications: Notifications fetch successful, count:', response.data.length);
            setNotifications(response.data);
            setUnreadCount(response.data.filter(n => !n.is_read).length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            
            // Handle specific error cases
            if (error.response?.status === 422) {
                console.log('🔍 useNotifications: 422 error - likely invalid JWT token, user may need to re-login');
            } else if (error.response?.status === 401) {
                console.log('🔍 useNotifications: 401 error - unauthorized, token may be expired');
            }
            
            setNotifications([]);
            setUnreadCount(0);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            if (!isValidToken(token)) {
                console.log('🔍 useNotifications: Invalid token for markAsRead');
                return;
            }

            await axios.post(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            // Update local state
            setNotifications(prev => 
                prev.map(notification => 
                    notification.id === notificationId 
                        ? { ...notification, is_read: true }
                        : notification
                )
            );
            
            // Update unread count
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        const unreadNotifications = notifications.filter(n => !n.is_read);
        
        try {
            const token = localStorage.getItem('token');
            if (!isValidToken(token)) {
                console.log('🔍 useNotifications: Invalid token for markAllAsRead');
                return;
            }
            
            // Mark all unread notifications as read
            await Promise.all(
                unreadNotifications.map(notification =>
                    axios.post(`${API_URL}/api/notifications/${notification.id}/read`, {}, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                )
            );
            
            // Update local state
            setNotifications(prev => 
                prev.map(notification => ({ ...notification, is_read: true }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    // Only fetch notifications when user is authenticated and token is available
    useEffect(() => {
        if (isAuthenticated && authToken) {
            console.log('🔍 useNotifications: Authentication confirmed, fetching notifications after 500ms delay...');
            // Add a small delay to ensure authentication is fully complete
            const timer = setTimeout(() => {
                fetchNotifications();
            }, 500);
            
            return () => clearTimeout(timer);
        } else {
            console.log('🔍 useNotifications: Not authenticated or no token, skipping notifications fetch');
        }
    }, [isAuthenticated, authToken]);

    return {
        notifications,
        loading,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        setNotifications,
        setUnreadCount
    };
}; 